import React from "react";
import * as Icon from "react-feather";

import JaneReportService from "../../../../services/JaneReportService";
import UtilityService from "../../../../services/UtilityService";
import { JOURNAL_SETTINGS, NO_PAIRING_CHOICE } from "../../../../constants/JaneReports";

import WComponent from "../../../common/WComponent";
import Loader from "../../../common/loader/Loader";

import ToolsCollapsiblePanel from "../kit/ToolsCollapsiblePanel";
import ToolsMappingRow from "../kit/ToolsMappingRow";
import ToolsNotice from "../kit/ToolsNotice";
import ToolsProgressBadge from "../kit/ToolsProgressBadge";

class JournalShapePanel extends WComponent {
	constructor(props) {
		super(props);

		this.state = {
			isLoading: true,
			isSaving: false,
			settings: [],
			proposal: null,
			proposalUnavailableBecause: "",
			needsReasonWhenNone: [],
			needsAMonth: [],
			startingPoints: [],
			pendingChoice: null,
			reasonEntry: "",
			monthEntry: "",
			errorMessage: ""
		};
	}

	async componentDidMount() {
		await this.load();
	}

	async componentDidUpdate(previousProps) {
		if (previousProps.periodStart !== this.props.periodStart) {
			await this.load();
		}
	}

	load = async () => {
		const { clientId, periodStart, onChanged } = this.props;

		await this.update({ isLoading: true, errorMessage: "" });
		const result = await JaneReportService.readJournalShape({ clientId, periodStart });

		if (result.error) {
			await this.update({ isLoading: false, errorMessage: result.error });
			return;
		}

		await this.update({
			isLoading: false,
			settings: result.settings,
			proposal: result.proposal,
			proposalUnavailableBecause: result.proposalUnavailableBecause || "",
			needsReasonWhenNone: result.needsReasonWhenNone || [],
			needsAMonth: result.needsAMonth || [],
			startingPoints: result.startingPoints || []
		});

		if (onChanged) {
			await onChanged({ settings: result.settings, reportsNeeded: result.reportsNeeded || [] });
		}
	};

	onSettingChosen = async ({ chosen, reasons, effectiveFrom }) => {
		const { clientId, periodStart, onChanged } = this.props;
		const { needsReasonWhenNone, needsAMonth } = this.state;

		const settingsNeedingReason = Object.keys(chosen).filter(attribute => chosen[attribute] === null && needsReasonWhenNone.includes(attribute));
		const settingsNeedingMonth = Object.keys(chosen).filter(attribute => needsAMonth.includes(attribute));
		const isReasonStillOwed = settingsNeedingReason.length > 0 && !reasons;
		const isMonthStillOwed = settingsNeedingMonth.length > 0 && !effectiveFrom;
		if (isReasonStillOwed || isMonthStillOwed) {
			const monthBeingBuilt = UtilityService.formatDate({ value: periodStart || new Date(), format: "YYYY-MM" });
			await this.update({ pendingChoice: { chosen, settingsNeedingReason, settingsNeedingMonth }, reasonEntry: "", monthEntry: monthBeingBuilt });
			return;
		}

		await this.update({ isSaving: true });
		const result = await JaneReportService.saveJournalShape({ clientId, settings: chosen, reasons: reasons || {}, effectiveFrom, periodStart });
		await this.update({ isSaving: false });

		if (result.error) {
			UtilityService.alert({ type: "error", text: result.error });
			return;
		}

		await this.update({
			settings: result.settings,
			proposal: result.proposal,
			proposalUnavailableBecause: result.proposalUnavailableBecause || "",
			needsReasonWhenNone: result.needsReasonWhenNone || [],
			needsAMonth: result.needsAMonth || [],
			pendingChoice: null,
			reasonEntry: "",
			monthEntry: ""
		});

		if (onChanged) {
			await onChanged({ settings: result.settings, reportsNeeded: result.reportsNeeded || [], wasJustSaved: true });
		}
	};

	onChoiceConfirmed = async () => {
		const { pendingChoice, reasonEntry, monthEntry } = this.state;

		const reasons = {};
		for (const attribute of pendingChoice.settingsNeedingReason) {
			reasons[attribute] = reasonEntry.trim();
		}
		const effectiveFrom = pendingChoice.settingsNeedingMonth.length > 0 ? `${monthEntry}-01` : undefined;

		await this.onSettingChosen({ chosen: pendingChoice.chosen, reasons, effectiveFrom });
	};

	onStartingPointChosen = async ({ startingPoint }) => {
		await this.onSettingChosen({ chosen: startingPoint.settings });
	};

	onProposalAccepted = async () => {
		const { settings, proposal } = this.state;

		const chosen = {};
		for (const setting of settings) {
			const isProposed = setting.attribute in proposal;
			if (!setting.isDecided && setting.appliesNow && isProposed) {
				chosen[setting.attribute] = proposal[setting.attribute];
			}
		}

		await this.onSettingChosen({ chosen });
	};

	describeEvidence = ({ attribute }) => {
		const { proposal } = this.state;
		const evidence = proposal ? proposal.evidence : null;
		const journalsRead = evidence ? evidence.journalsRead : 0;

		if (journalsRead === 0) {
			return "No previous Jane journals to learn from.";
		}

		const revenueAccountsCredited = evidence.revenueAccountsCredited;
		const evidenceByAttribute = {
			account: `Your last ${journalsRead} Jane journals credited ${revenueAccountsCredited} revenue ${revenueAccountsCredited === 1 ? "account" : "accounts"}.`,
			site: `${evidence.linesWithLocation} revenue lines in your last ${journalsRead} Jane journals carried a QuickBooks location.`,
			receivable: `${evidence.linesOnReceivable} lines in your last ${journalsRead} Jane journals debited an accounts receivable account.`
		};
		const sentence = evidenceByAttribute[attribute];
		return sentence;
	};

	renderSetting = ({ definition }) => {
		const { settings, proposal, isSaving, needsReasonWhenNone } = this.state;
		const { attribute } = definition;

		const setting = settings.find(candidate => candidate.attribute === attribute);
		if (setting && !setting.appliesNow) {
			return (
				<div className="tk-list__row" key={attribute}>
					<div className="tk-list__main">
						<div className="tk-list__title">{definition.label}</div>
						<div className="tk-list__subtitle">{setting.notApplyingBecause}</div>
					</div>
				</div>
			);
		}

		const isDecided = Boolean(setting && setting.isDecided);
		const chosenValue = isDecided ? setting.sourceColumn || NO_PAIRING_CHOICE : null;
		const decidedOn = isDecided ? UtilityService.formatDate({ value: setting.decidedAt }) : "";
		const whoDecidedText = isDecided && setting.decidedByName ? `decided by ${setting.decidedByName} on ${decidedOn}` : `decided on ${decidedOn} · who decided wasn't recorded then`;
		const hasReason = isDecided && Boolean(setting.reason);
		const isNoneAgainstEvidence = isDecided && !setting.sourceColumn && needsReasonWhenNone.includes(attribute);
		let reasonText = "";
		if (hasReason) {
			reasonText = ` · "${setting.reason}"`;
		} else if (isNoneAgainstEvidence) {
			reasonText = " · no reason recorded";
		}
		const fromText = isDecided && setting.effectiveFrom ? ` · from ${UtilityService.formatDate({ value: setting.effectiveFrom, format: "MMMM YYYY" })}` : "";
		const decidedText = isDecided ? `${whoDecidedText}${fromText}${reasonText}` : "not decided yet";

		const proposedValue = proposal && !isDecided ? proposal[attribute] || NO_PAIRING_CHOICE : null;
		const proposedChoice = definition.choices.find(choice => choice.value === proposedValue);
		const suggestion = proposedChoice ? { value: proposedChoice.value, label: proposedChoice.label, evidence: this.describeEvidence({ attribute }), isCertain: false } : null;

		const toSourceColumn = value => (value === NO_PAIRING_CHOICE ? null : value);

		return (
			<ToolsMappingRow
				key={attribute}
				isChosen={isDecided}
				label={definition.label}
				sublabel={decidedText}
				options={definition.choices}
				value={chosenValue}
				placeholder="Choose…"
				searchPlaceholder="Search…"
				disabled={isSaving}
				onChange={value => this.onSettingChosen({ chosen: { [attribute]: toSourceColumn(value) } })}
				suggestion={suggestion}
				onAcceptSuggestion={() => this.onSettingChosen({ chosen: { [attribute]: toSourceColumn(proposedChoice.value) } })}
			/>
		);
	};

	renderStartingPoints = () => {
		const { startingPoints, isSaving } = this.state;

		if (startingPoints.length === 0) {
			return null;
		}

		const fillsText = startingPoints.map(startingPoint => `${startingPoint.label} — ${startingPoint.fills}.`).join(" ");

		return (
			<div className="tk-suggest-bar">
				<span className="tk-suggest-bar__text">Start from a common shape, then change any setting below. {fillsText}</span>
				<span className="tk-action-row">
					{startingPoints.map(startingPoint => (
						<button
							key={startingPoint.key}
							className="tk-button tk-button--secondary tk-button--compact"
							onClick={() => this.onStartingPointChosen({ startingPoint })}
							type="button"
							disabled={isSaving}
						>
							{startingPoint.label}
						</button>
					))}
				</span>
			</div>
		);
	};

	renderChoicePrompt = () => {
		const { pendingChoice, reasonEntry, monthEntry, isSaving } = this.state;

		if (!pendingChoice) {
			return null;
		}

		const definitionOf = attribute => JOURNAL_SETTINGS.find(candidate => candidate.attribute === attribute);
		const isReasonNeeded = pendingChoice.settingsNeedingReason.length > 0;
		const isMonthNeeded = pendingChoice.settingsNeedingMonth.length > 0;
		const reasonFacts = pendingChoice.settingsNeedingReason.map(attribute => (
			<div key={attribute}>
				{definitionOf(attribute).label}: {this.describeEvidence({ attribute })} {definitionOf(attribute).noneConsequence}
			</div>
		));
		const monthFacts = pendingChoice.settingsNeedingMonth.map(attribute => <div key={attribute}>{definitionOf(attribute).monthPrompt}</div>);
		const isReasonBlank = reasonEntry.trim() === "";
		const isMonthBlank = !/^\d{4}-\d{2}$/.test(monthEntry);
		const title = isReasonNeeded ? "Your previous Jane journals recorded this" : "From which month?";

		return (
			<ToolsNotice tone="warning" icon={<Icon.AlertTriangle size={16} />} title={title} detail={[...reasonFacts, ...monthFacts]}>
				{isMonthNeeded && (
					<input
						className="tk-input tk-input--short"
						type="month"
						aria-label="The first month this applies to"
						value={monthEntry}
						onChange={({ target }) => this.update({ monthEntry: target.value })}
					/>
				)}
				{isReasonNeeded && (
					<textarea
						className="tk-input tk-input--multiline"
						aria-label="Why this client stops recording it"
						placeholder="Why does this client stop recording it? The reason is saved with the setting and shown beside it."
						value={reasonEntry}
						onChange={({ target }) => this.update({ reasonEntry: target.value })}
					/>
				)}
				<div className="tk-action-row">
					<button
						className="tk-button tk-button--primary tk-button--compact"
						onClick={this.onChoiceConfirmed}
						type="button"
						disabled={isSaving || (isReasonNeeded && isReasonBlank) || (isMonthNeeded && isMonthBlank)}
					>
						{isReasonNeeded ? "Save with this reason" : "Save from this month"}
					</button>
					<button className="tk-text-action" onClick={() => this.update({ pendingChoice: null, reasonEntry: "", monthEntry: "" })} type="button" disabled={isSaving}>
						Cancel
					</button>
				</div>
			</ToolsNotice>
		);
	};

	render() {
		const { isLoading, settings, proposal, errorMessage } = this.state;

		if (isLoading) {
			return (
				<div className="tk-panel tk-panel-loading">
					<Loader />
					<span className="tk-text-muted">Reading this client's journal settings…</span>
				</div>
			);
		}

		const settingsThatApply = settings.filter(setting => setting.appliesNow);
		const undecidedCount = settingsThatApply.filter(setting => !setting.isDecided).length;
		const isAnythingProposed = Boolean(proposal) && settingsThatApply.some(setting => !setting.isDecided && setting.attribute in proposal);
		const canAcceptProposal = undecidedCount > 0 && isAnythingProposed;

		return (
			<ToolsCollapsiblePanel
				title="How this client's journal is shaped"
				defaultOpen={undecidedCount > 0}
				status={<ToolsProgressBadge remainingCount={undecidedCount} totalCount={settingsThatApply.length} doneWord="decided" todoWord="decide" />}
			>
				<div className="tk-lede">
					When revenue is recognised, what fills the revenue account, where each Jane site is recorded on the line, and whether the journal
					books a receivable. Decided once for this client and shown here with who decided it, so a choice made to get past a warning stays
					visible and can be changed.
				</div>

				{this.renderStartingPoints()}

				{this.state.proposalUnavailableBecause && (
					<div className="tk-note">Nothing is proposed from this client's own journals: {this.state.proposalUnavailableBecause}</div>
				)}

				{canAcceptProposal && (
					<div className="tk-suggest-bar">
						<span className="tk-suggest-bar__text">Proposed from this client's own previous Jane journals. Check each before accepting.</span>
						<button className="tk-button tk-button--secondary tk-button--compact" onClick={this.onProposalAccepted} type="button">
							Use the proposal
						</button>
					</div>
				)}

				<div>{JOURNAL_SETTINGS.map(definition => this.renderSetting({ definition }))}</div>

				{this.renderChoicePrompt()}

				{errorMessage && <div className="tk-note">{errorMessage}</div>}
			</ToolsCollapsiblePanel>
		);
	}
}

export default JournalShapePanel;
